// /////////////////////////////////////////////////////////////
// [ファイル] useBuildingTable.ts
// [概要] 顧客管理『建物住所』タブの一覧データフック。
//        useCrmTable と同じ構造 (requestId による競合ガード・
//        フィルター/ソート/ページ状態管理) で get_crm_buildings を呼ぶ。
//        建物タブが一度も開かれていない間は初回フェッチを保留し、
//        タブ有効化 (enabled=true) を検知した時点で取得を開始する。
// /////////////////////////////////////////////////////////////
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';
import type { SortConfig } from './useCrmTable';

export interface BuildingFilters {
  search_word?: string;
  // 登録経路 ('' すべて / manual 手動登録のみ / customer_auto 簡易登録のみ)
  registration_source?: string;
  // true で関係者・登記・SFA 案件のいずれからも参照されていない建物のみ表示 (ゴミ掃除用)
  orphan_only?: boolean;
  [key: string]: any;
}

// 既定は手動登録のみ表示 (顧客フォームの簡易登録で自動作成された建物は既定で非表示)
export const DEFAULT_BUILDING_FILTERS: BuildingFilters = {
  search_word: '',
  registration_source: 'manual',
};

const GET_BUILDINGS_URL = ep('get_crm_buildings');

export const useBuildingTable = (
  enabled: boolean,
  initialFilters: BuildingFilters = DEFAULT_BUILDING_FILTERS,
  initialSort: SortConfig = { field: 'updated_at', order: 'desc' }
) => {
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<BuildingFilters>(initialFilters);
  const [sort, setSort] = useState<SortConfig>(initialSort);

  // 一度でもタブが有効化されたら true のまま維持する。
  // タブ往復のたびに再フェッチせず、取得済みデータ・ページ位置を保持するため。
  const [activated, setActivated] = useState(enabled);
  useEffect(() => {
    if (enabled && !activated) setActivated(true);
  }, [enabled, activated]);

  // 競合状態（Race Condition）を防ぐためのリクエスト管理用 ref
  const lastRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++lastRequestId.current;
    setLoading(true);
    setData([]);

    try {
      const response = await axios.post(GET_BUILDINGS_URL, {
        page,
        pageSize,
        filters,
        sortField: sort.field,
        sortOrder: sort.order,
      });

      if (requestId === lastRequestId.current && response.data.status === 'success') {
        setData(response.data.rowsResult || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      if (requestId === lastRequestId.current) {
        console.error('Error fetching building data:', error);
      }
    } finally {
      if (requestId === lastRequestId.current) {
        setLoading(false);
      }
    }
  }, [page, pageSize, filters, sort]);

  useEffect(() => {
    if (!activated) return;
    fetchData();
  }, [activated, fetchData]);

  const updateFilters = useCallback((newFilters: BuildingFilters) => {
    setLoading(true);
    setData([]);
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSort = useCallback((field: string) => {
    setLoading(true);
    setData([]);
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
    setPage(0);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setLoading(true);
    setData([]);
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setLoading(true);
    setData([]);
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  return {
    data,
    totalCount,
    loading,
    page,
    pageSize,
    filters,
    sort,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    updateFilters,
    handleSort,
    refresh: fetchData
  };
};
